import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Banknote, CheckCircle2, CreditCard, QrCode,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { ORDER_STATUS } from '../../api/orders';
import { confirmPod, deliverOrder, DELIVERY_ERRORS } from '../../api/delivery';
import { formatCurrency } from '../../data/mockData';
import Button from '../../components/Button';
import DriverShell from './DriverShell';
import DriverBillSummary from './DriverBillSummary';
import DriverSuccessModal from './DriverSuccessModal';
import { useDriverOrder } from './useDriverOrder';
import cardStyles from './DriverCards.module.css';
import styles from './DriverCompleteDelivery.module.css';

const PAYMENT_METHODS = [
  { key: 'CASH', label: 'Cash', icon: Banknote },
  { key: 'UPI', label: 'UPI QR', icon: QrCode },
  { key: 'CARD', label: 'Card', icon: CreditCard },
];

const CONFIRM_LABEL = {
  CASH: 'Cash collected',
  UPI: 'UPI payment received',
  CARD: 'Card payment received',
};

function friendlyError(err) {
  return DELIVERY_ERRORS[err.message] || err.message || 'Could not complete the delivery — try again.';
}

export default function DriverCompleteDelivery() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { notify } = useOrders();
  const { order } = useDriverOrder(orderId);
  const podPhotoKey = location.state?.podPhotoKey || null;

  const [payMethod, setPayMethod] = useState('CASH');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [delivered, setDelivered] = useState(false);

  const isCOD = order?.paymentMethod === 'COD';
  const canDeliver = order?.status === ORDER_STATUS.OUT_FOR_DELIVERY
    && !!podPhotoKey
    && (!isCOD || paymentConfirmed);

  const selectPayMethod = (key) => {
    setPayMethod(key);
    setPaymentConfirmed(false);
  };

  const handleDeliver = async () => {
    if (!canDeliver) return;
    setDelivering(true);
    try {
      await confirmPod(order.id, { photoKey: podPhotoKey }, user.idToken);
      await deliverOrder(order.id, { codCollected: true }, user.idToken);
      setDelivered(true);
    } catch (err) {
      notify(friendlyError(err));
    } finally {
      setDelivering(false);
    }
  };

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={cardStyles.headerTitle}>Complete Delivery</h1>
      </header>

      {order && (
        <>
          <main className={cardStyles.content}>
            <section className={cardStyles.card}>
              <p className={cardStyles.cardLabel}>Proof of Delivery</p>
              <div className={styles.proofRow}>
                <CheckCircle2 size={18} className={podPhotoKey ? styles.proofIconDone : styles.proofIconMissing} />
                <span>{podPhotoKey ? 'Photo uploaded' : 'No photo captured'}</span>
              </div>
              {!podPhotoKey && (
                <Button
                  size="sm"
                  variant="outline"
                  className={styles.retakeBtn}
                  onClick={() => navigate(`/driver/rides/${order.id}/proof`, { state: { order } })}
                >
                  Capture photo
                </Button>
              )}
            </section>

            <DriverBillSummary order={order} title="Bill Summary" />

            {isCOD && (
              <section className={cardStyles.card}>
                <p className={cardStyles.cardLabel}>Collect Payment</p>

                <div className={styles.methodRow}>
                  {PAYMENT_METHODS.map(({ key, label, icon: Icon }) => (
                    <button
                      type="button"
                      key={key}
                      className={`${styles.methodOption} ${payMethod === key ? styles.methodOptionActive : ''}`}
                      onClick={() => selectPayMethod(key)}
                    >
                      <Icon size={15} />
                      {label}
                    </button>
                  ))}
                </div>

                {payMethod === 'UPI' && (
                  <div className={styles.qrBox}>
                    <QrCode size={72} strokeWidth={1} />
                    <p className={styles.qrCaption}>Show this code to the customer</p>
                    <p className={styles.qrAmount}>{formatCurrency(order.total)}</p>
                  </div>
                )}

                {payMethod === 'CARD' && (
                  <div className={styles.cardMachineBox}>
                    <CreditCard size={22} />
                    <p>Charge {formatCurrency(order.total)} on your card machine</p>
                  </div>
                )}

                <div className={styles.codRow}>
                  <span>{CONFIRM_LABEL[payMethod]} ({formatCurrency(order.total)})</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={paymentConfirmed}
                    aria-label={CONFIRM_LABEL[payMethod]}
                    className={`${styles.toggle} ${paymentConfirmed ? styles.toggleOn : ''}`}
                    onClick={() => setPaymentConfirmed((v) => !v)}
                  >
                    <span className={styles.toggleThumb} />
                  </button>
                </div>
              </section>
            )}

            {canDeliver ? (
              <Button size="lg" variant="primary" className={styles.deliverBtn} disabled={delivering} onClick={handleDeliver}>
                {delivering ? 'Delivering…' : 'Deliver'}
              </Button>
            ) : (
              <button type="button" className={styles.deliverDisabled} disabled>
                Deliver
              </button>
            )}

            <button
              type="button"
              className={styles.failLink}
              onClick={() => navigate(`/driver/rides/${order.id}/fail`, { state: { order } })}
            >
              <AlertCircle size={13} /> Unable to complete delivery?
            </button>
          </main>

          {delivered && (
            <DriverSuccessModal
              icon={CheckCircle2}
              title="Delivered!"
              text={`Order ${order.id} has been marked as delivered.`}
              buttonLabel="Back to My Runs"
              onButtonClick={() => navigate('/driver', { replace: true })}
            />
          )}
        </>
      )}
    </DriverShell>
  );
}
